import { bool, object, oneOf } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { MaxTimeModifiers } from "@repo/utils";
import { CELL_WIDTHS } from "@utils";
import { ClearInputSimple } from "@components";
import { ResetValueButton, useEstimateStructure } from "@tools";
import { makeStyles } from "@material-ui/styles";

import { TransferWorkload } from "@tools/EstimateEditor/components";
import classnames from "classnames";

export const TimelineDistributionCell = observer(({
  element,
  period,
  dividerStyle,
  allowEdition,
  previousPeriod,
  nextPeriod,
  shrinkWidth,
}) => {
  const structure = useEstimateStructure();
  const { t } = useTranslation();
  const classes = useStyle({ fontColor: element.fontColor, shrinkWidth });

  const {
    usedRoundDecimals,
    usedThousandSeparator,
  } = structure.settings;

  const value = period.value / MaxTimeModifiers.HOUR;

  const handleValueChange = (newValue) => {
    try {
      element.setCustomDailyWorkload(
        period.type,
        period.startDate,
        (newValue || 0) * MaxTimeModifiers.HOUR,
      );
    } catch {
      return true;
    }
  };

  const handleReset = () => {
    element.resetCustomDailyWorkload(period.type, period.startDate);
  };

  return (
    <div
      className={classnames("cell", classes.cell, dividerStyle && classes[dividerStyle], "relative")}
      onClick={(event) => event.stopPropagation()}
    >
      {allowEdition && !element.isAutomated && previousPeriod &&
        <TransferWorkload
          source={element}
          target={element}
          sourcePeriod={previousPeriod}
          targetPeriod={period}
          placement="left"
          size="small"
        />
      }
      {allowEdition ? (
        <ClearInputSimple
          value={value}
          onChange={handleValueChange}
          showDecimals
          size="small"
          decimals={usedRoundDecimals}
          thousandSeparator={usedThousandSeparator}
          placeholder={parseFloat(value.toFixed(2))}
          placeholderColor={element.fontColor}
          borderColor={element.fontColor}
          clickDialogMessageTitle={element.isAutomated ? t("views.editor.timeline_table.percent_value") : undefined}
          clickDialogMessageContent={element.isAutomated ? t("views.editor.timeline_table.automated_distribution_uneditable") : undefined}
        />
      ) : (
        <NumericFormat
          displayType="text"
          value={value}
          thousandSeparator={usedThousandSeparator}
          decimalSeparator="."
          decimalScale={usedRoundDecimals}
          valueIsNumericString
          className={classes.value}
        />
      )}
      {allowEdition && !element.isAutomated && nextPeriod &&
        <TransferWorkload
          source={element}
          target={element}
          sourcePeriod={period}
          targetPeriod={nextPeriod}
          placement="right"
          size="small"
        />
      }
      {allowEdition && !element.isAutomated && period.ov && element.resetCustomDailyWorkload && (
        <ResetValueButton
          action={handleReset}
          className={classes.reset}
          size="small"
        >
          {t("views.editor.revert_value")}
        </ResetValueButton>
      )}
    </div>
  );
});

TimelineDistributionCell.propTypes = {
  element: object.isRequired,
  period: object.isRequired,
  dividerStyle: oneOf(["dividerSolid", "dividerDashed", "dividerDotted"]),
  allowEdition: bool,
  previousPeriod: object,
  nextPeriod: object,
};

const useStyle = makeStyles(() => ({
  cell: {
    alignItems: "center",
    display: "flex",
    height: "calc(100% - 8px)",
    width: ({ shrinkWidth }) => CELL_WIDTHS.VALUE.SMALL - (shrinkWidth || 0),
    justifyContent: "center",
    color: "inherit",
    transition: "width 250ms",
  },
  dividerSolid: ({ fontColor }) => ({
    borderRight: `1px solid ${fontColor}`,
  }),
  dividerDashed: ({ fontColor }) => ({
    borderRight: `1px dashed ${fontColor}`,
  }),
  dividerDotted: ({ fontColor }) => ({
    borderRight: `1px dotted ${fontColor}`,
  }),
  value: ({ fontColor }) => ({
    color: fontColor,
    textAlign: "center",
    width: "100%",
    padding: 6,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "transparent",
    textOverflow: "clip",
    overflow: "hidden",
  }),
  reset: {
    position: "absolute",
    right: 4,
    bottom: -4,
    zIndex: 9,
    backgroundColor: "rgba(255, 255, 255, 0.6) !important",
    borderRadius: "50%",
    boxShadow: "0 0 2px rgba(0, 0, 0, 0.2)",
  },
}));
